import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/store";
import {
  EventEnrollmentItemResponseDTO,
  FetchEventEnrollmentsRequest,
} from "@/modules/eventRegistration/features/EventWaitlist/api/fetchEventEnrollments/types";
import {
  EventWaitlistItemResponse,
  FetchEventWaitlistItemsRequest,
} from "@/modules/eventRegistration/features/EventWaitlist/api/fetchEventWaitlistItems/types";
import {
  FetchWaitlistDetailsRequest,
  WaitlistDetails,
} from "@/modules/eventRegistration/features/EventWaitlist/api/fetchWaitlistDetails/types";
import { fetchEventEnrollments } from "@/modules/eventRegistration/features/EventWaitlist/api/fetchEventEnrollments/fetchEventEnrollmentsApi";
import { fetchEventWaitlistItems } from "@/modules/eventRegistration/features/EventWaitlist/api/fetchEventWaitlistItems/fetchEventWaitlistItemsApi";
import { fetchWaitlistDetails } from "@/modules/eventRegistration/features/EventWaitlist/api/fetchWaitlistDetails/fetchWaitlistDetailsApi";
import {
  MoveWaitlistToEnrollmentRequest,
  MoveWaitlistToEnrollmentResponse,
  MoveWaitlistToEnrollmentResult,
} from "@/modules/eventRegistration/features/EventWaitlist/api/moveWaitlistToEnrollment/types";
import {
  RemoveWaitlistItemRequest,
  RemoveWaitlistItemResponse,
  RemoveWaitlistItemResult,
} from "@/modules/eventRegistration/features/EventWaitlist/api/removeWaitlistItem/types";
import {
  ReplaceEnrollmentAttendeeRequest,
  ReplaceEnrollmentAttendeeResponse,
  ReplaceEnrollmentAttendeeResult,
} from "@/modules/eventRegistration/features/EventWaitlist/api/replaceEnrollmentAttendee/types";
import {
  ReplaceEnrollmentWithEmployeeRequest,
  ReplaceEnrollmentWithEmployeeResponse,
  ReplaceEnrollmentWithEmployeeResult,
} from "@/modules/eventRegistration/features/EventWaitlist/api/replaceEnrollmentWithEmployee/types";
import {
  UpdateWaitlistPositionRequest,
  UpdateWaitlistPositionResponse,
  UpdateWaitlistPositionResult,
} from "@/modules/eventRegistration/features/EventWaitlist/api/updateWaitlistPosition/types";
import { removeWaitlistItem } from "@/modules/eventRegistration/features/EventWaitlist/api/removeWaitlistItem/removeWaitlistItemApi";
import { replaceEnrollmentAttendee } from "@/modules/eventRegistration/features/EventWaitlist/api/replaceEnrollmentAttendee/replaceEnrollmentAttendeeApi";
import { replaceEnrollmentWithEmployee } from "@/modules/eventRegistration/features/EventWaitlist/api/replaceEnrollmentWithEmployee/replaceEnrollmentWithEmployeeApi";
import { updateWaitlistPosition } from "@/modules/eventRegistration/features/EventWaitlist/api/updateWaitlistPosition/updateWaitlistPositionApi";
import {
  MoveEnrollmentToWaitlistRequest,
  MoveEnrollmentToWaitlistResponse,
  MoveEnrollmentToWaitlistResult,
} from "@/modules/eventRegistration/features/EventWaitlist/api/moveEnrollmentToWaitlist/types";
import { moveEnrollmentToWaitlist } from "@/modules/eventRegistration/features/EventWaitlist/api/moveEnrollmentToWaitlist/moveEnrollmentToWaitlistApi";
import { moveWaitlistToEnrollment } from "@/modules/eventRegistration/features/EventWaitlist/api/moveWaitlistToEnrollment/moveWaitlistToEnrollmentApi";
import {
  EventSessionAttendancesByDate,
  FetchEventAttendancesRequest,
  AttendanceGridItem,
} from "@/modules/eventRegistration/features/EventWaitlist/api/fetchEventAttendences/types";
import {
  fetchEventAttendances,
  fetchAttendanceGrid,
} from "@/modules/eventRegistration/features/EventWaitlist/api/fetchEventAttendences/fetchEventAttendancesApi";
import { updateEventSessionAttendance } from "@/modules/eventRegistration/features/EventWaitlist/api/updateEventSessionAttendance/updateEventSessionAttendanceApi";
import { UpdateEventSessionAttendanceRequest } from "@/modules/eventRegistration/features/EventWaitlist/api/updateEventSessionAttendance/types";
import { exportEventAttendanceCsv } from "@/modules/eventRegistration/features/EventWaitlist/api/exportEventAttendanceCsv/exportEventAttendanceCsvApi";
import { ExportEventAttendanceCsvRequest } from "@/modules/eventRegistration/features/EventWaitlist/api/exportEventAttendanceCsv/types";
import { bulkUpdateEventSessionAttendance } from "@/modules/eventRegistration/features/EventWaitlist/api/bulkUpdateEventSessionAttendance/bulkUpdateEventSessionAttendanceApi";
import { BulkUpdateEventSessionAttendanceRequest } from "@/modules/eventRegistration/features/EventWaitlist/api/bulkUpdateEventSessionAttendance/types";
import { finalizeEventSession } from "@/modules/eventRegistration/features/EventWaitlist/api/finalizeEventSession/finalizeEventSessionApi";
import { FinalizeEventSessionRequest } from "@/modules/eventRegistration/features/EventWaitlist/api/finalizeEventSession/types";

interface EnrollmentsWaitlistSliceState {
  loadingEnrollments: boolean;
  enrollmentsError: string | null;
  enrollments: EventEnrollmentItemResponseDTO[];
  totalEnrollments: number;
  loadingWaitlistItems: boolean;
  waitlistItemsError: string | null;
  waitlistItems: EventWaitlistItemResponse[];
  totalWaitlistItems: number;
  loadingWaitlistDetails: boolean;
  waitlistDetailsError: string | null;
  waitlistDetails: WaitlistDetails | null;

  loadingMoveWaitlist: boolean;
  errorMoveWaitlist: string | null;
  moveWaitlistResult: MoveWaitlistToEnrollmentResult | null;

  loadingRemoveWaitlist: boolean;
  errorRemoveWaitlist: string | null;
  removeWaitlistResult: RemoveWaitlistItemResult | null;

  loadingReplaceAttendee: boolean;
  errorReplaceAttendee: string | null;
  replaceAttendeeResult: ReplaceEnrollmentAttendeeResult | null;

  loadingReplaceWithEmployee: boolean;
  errorReplaceWithEmployee: string | null;
  replaceWithEmployeeResult: ReplaceEnrollmentWithEmployeeResult | null;

  loadingUpdatePosition: boolean;
  errorUpdatePosition: string | null;
  updatePositionResult: UpdateWaitlistPositionResult | null;

  loadingMoveEnrollment: boolean;
  errorMoveEnrollment: string | null;
  moveEnrollmentResult: MoveEnrollmentToWaitlistResult | null;

  loadingAttendances: boolean;
  attendancesError: string | null;
  attendances: EventSessionAttendancesByDate;
  totalAttendances: number;
  selectedAttendanceStatus: string | null;

  loadingAttendanceGrid: boolean;
  attendanceGridError: string | null;
  attendanceGridItems: AttendanceGridItem[];
  attendanceGridSessionDays: string[];
  totalAttendanceGridItems: number;

  loadingUpdateAttendance: boolean;
  errorUpdateAttendance: string | null;

  loadingExportCsv: boolean;
  errorExportCsv: string | null;

  loadingBulkUpdateAttendance: boolean;
  errorBulkUpdateAttendance: string | null;

  loadingFinalizeSession: boolean;
  errorFinalizeSession: string | null;
  sessionFinalized: boolean;
}

const initialState: EnrollmentsWaitlistSliceState = {
  loadingEnrollments: false,
  enrollmentsError: null,
  enrollments: [],
  totalEnrollments: 0,

  loadingWaitlistItems: false,
  waitlistItemsError: null,
  waitlistItems: [],
  totalWaitlistItems: 0,

  loadingWaitlistDetails: false,
  waitlistDetailsError: null,
  waitlistDetails: null,

  loadingMoveWaitlist: false,
  errorMoveWaitlist: null,
  moveWaitlistResult: null,

  loadingRemoveWaitlist: false,
  errorRemoveWaitlist: null,
  removeWaitlistResult: null,

  loadingReplaceAttendee: false,
  errorReplaceAttendee: null,
  replaceAttendeeResult: null,

  loadingReplaceWithEmployee: false,
  errorReplaceWithEmployee: null,
  replaceWithEmployeeResult: null,

  loadingUpdatePosition: false,
  errorUpdatePosition: null,
  updatePositionResult: null,

  loadingMoveEnrollment: false,
  errorMoveEnrollment: null,
  moveEnrollmentResult: null,

  loadingAttendances: false,
  attendancesError: null,
  attendances: {},
  totalAttendances: 0,
  selectedAttendanceStatus: null,

  loadingAttendanceGrid: false,
  attendanceGridError: null,
  attendanceGridItems: [],
  attendanceGridSessionDays: [],
  totalAttendanceGridItems: 0,

  loadingUpdateAttendance: false,
  errorUpdateAttendance: null,

  loadingExportCsv: false,
  errorExportCsv: null,

  loadingBulkUpdateAttendance: false,
  errorBulkUpdateAttendance: null,

  loadingFinalizeSession: false,
  errorFinalizeSession: null,
  sessionFinalized: false,
};

const enrollmentsWaitlistSlice = createSlice({
  name: "enrollmentsWaitlist",
  initialState,
  reducers: {
    setLoadingEnrollments(state, action: PayloadAction<boolean>) {
      state.loadingEnrollments = action.payload;
    },
    setEnrollmentsError(state, action: PayloadAction<string | null>) {
      state.enrollmentsError = action.payload;
    },
    setEnrollments(
      state,
      action: PayloadAction<EventEnrollmentItemResponseDTO[]>,
    ) {
      state.enrollments = action.payload;
    },
    setTotalEnrollments(state, action: PayloadAction<number>) {
      state.totalEnrollments = action.payload;
    },
    setLoadingWaitlistItems(state, action: PayloadAction<boolean>) {
      state.loadingWaitlistItems = action.payload;
    },
    setWaitlistItemsError(state, action: PayloadAction<string | null>) {
      state.waitlistItemsError = action.payload;
    },
    setWaitlistItems(
      state,
      action: PayloadAction<EventWaitlistItemResponse[]>,
    ) {
      state.waitlistItems = action.payload;
    },
    setTotalWaitlistItems(state, action: PayloadAction<number>) {
      state.totalWaitlistItems = action.payload;
    },
    setLoadingWaitlistDetails(state, action: PayloadAction<boolean>) {
      state.loadingWaitlistDetails = action.payload;
    },
    setWaitlistDetailsError(state, action: PayloadAction<string | null>) {
      state.waitlistDetailsError = action.payload;
    },
    setWaitlistDetails(state, action: PayloadAction<WaitlistDetails | null>) {
      state.waitlistDetails = action.payload;
    },

    setLoadingMoveWaitlist(state, action: PayloadAction<boolean>) {
      state.loadingMoveWaitlist = action.payload;
    },
    setErrorMoveWaitlist(state, action: PayloadAction<string | null>) {
      state.errorMoveWaitlist = action.payload;
    },
    setMoveWaitlistResult(
      state,
      action: PayloadAction<MoveWaitlistToEnrollmentResult | null>,
    ) {
      state.moveWaitlistResult = action.payload;
    },

    setLoadingRemoveWaitlist(state, action: PayloadAction<boolean>) {
      state.loadingRemoveWaitlist = action.payload;
    },
    setErrorRemoveWaitlist(state, action: PayloadAction<string | null>) {
      state.errorRemoveWaitlist = action.payload;
    },
    setRemoveWaitlistResult(
      state,
      action: PayloadAction<RemoveWaitlistItemResult | null>,
    ) {
      state.removeWaitlistResult = action.payload;
    },

    setLoadingReplaceAttendee(state, action: PayloadAction<boolean>) {
      state.loadingReplaceAttendee = action.payload;
    },
    setErrorReplaceAttendee(state, action: PayloadAction<string | null>) {
      state.errorReplaceAttendee = action.payload;
    },
    setReplaceAttendeeResult(
      state,
      action: PayloadAction<ReplaceEnrollmentAttendeeResult | null>,
    ) {
      state.replaceAttendeeResult = action.payload;
    },

    setLoadingReplaceWithEmployee(state, action: PayloadAction<boolean>) {
      state.loadingReplaceWithEmployee = action.payload;
    },
    setErrorReplaceWithEmployee(state, action: PayloadAction<string | null>) {
      state.errorReplaceWithEmployee = action.payload;
    },
    setReplaceWithEmployeeResult(
      state,
      action: PayloadAction<ReplaceEnrollmentWithEmployeeResult | null>,
    ) {
      state.replaceWithEmployeeResult = action.payload;
    },

    setLoadingUpdatePosition(state, action: PayloadAction<boolean>) {
      state.loadingUpdatePosition = action.payload;
    },
    setErrorUpdatePosition(state, action: PayloadAction<string | null>) {
      state.errorUpdatePosition = action.payload;
    },
    setUpdatePositionResult(
      state,
      action: PayloadAction<UpdateWaitlistPositionResult | null>,
    ) {
      state.updatePositionResult = action.payload;
    },

    updateWaitlistItemPositionOptimistically(
      state,
      action: PayloadAction<{ waitlistId: number; newPosition: number }>,
    ) {
      const { waitlistId, newPosition } = action.payload;
      const itemIndex = state.waitlistItems.findIndex(
        (item) => item.id === waitlistId,
      );
      if (itemIndex === -1) return;
      let newWaitlistItems = [...state.waitlistItems];

      const [movedItem] = newWaitlistItems.splice(itemIndex, 1);

      movedItem.waitlistPosition = newPosition;
      const targetIndex = newPosition - 1;

      newWaitlistItems.splice(
        Math.min(targetIndex, newWaitlistItems.length),
        0,
        movedItem,
      );

      newWaitlistItems = newWaitlistItems.map((item, index) => ({
        ...item,
        waitlistPosition: index + 1,
      }));

      state.waitlistItems = newWaitlistItems;
    },

    setLoadingMoveEnrollment(state, action: PayloadAction<boolean>) {
      state.loadingMoveEnrollment = action.payload;
    },
    setErrorMoveEnrollment(state, action: PayloadAction<string | null>) {
      state.errorMoveEnrollment = action.payload;
    },
    setMoveEnrollmentResult(
      state,
      action: PayloadAction<MoveEnrollmentToWaitlistResult | null>,
    ) {
      state.moveEnrollmentResult = action.payload;
    },

    setLoadingAttendances(state, action: PayloadAction<boolean>) {
      state.loadingAttendances = action.payload;
    },
    setAttendancesError(state, action: PayloadAction<string | null>) {
      state.attendancesError = action.payload;
    },
    setAttendances(
      state,
      action: PayloadAction<EventSessionAttendancesByDate>,
    ) {
      state.attendances = action.payload;
    },
    setTotalAttendances(state, action: PayloadAction<number>) {
      state.totalAttendances = action.payload;
    },
    setSelectedAttendanceStatus(state, action: PayloadAction<string | null>) {
      state.selectedAttendanceStatus = action.payload;
    },

    setLoadingAttendanceGrid(state, action: PayloadAction<boolean>) {
      state.loadingAttendanceGrid = action.payload;
    },
    setAttendanceGridError(state, action: PayloadAction<string | null>) {
      state.attendanceGridError = action.payload;
    },
    setAttendanceGridItems(state, action: PayloadAction<AttendanceGridItem[]>) {
      state.attendanceGridItems = action.payload;
    },
    setAttendanceGridSessionDays(state, action: PayloadAction<string[]>) {
      state.attendanceGridSessionDays = action.payload;
    },
    setTotalAttendanceGridItems(state, action: PayloadAction<number>) {
      state.totalAttendanceGridItems = action.payload;
    },

    updateDayStatusOptimistically(
      state,
      action: PayloadAction<{ attendanceId: number; newStatus: string }>,
    ) {
      const { attendanceId, newStatus } = action.payload;
      for (const item of state.attendanceGridItems) {
        const dayStatus = item.dayStatuses.find(
          (ds) => ds.attendanceId === attendanceId,
        );
        if (dayStatus) {
          dayStatus.status = newStatus as "pending" | "present" | "no_show";
          // Recompute overall status
          const hasPresent = item.dayStatuses.some(
            (ds) => ds.status === "present",
          );
          const hasPending = item.dayStatuses.some(
            (ds) => ds.status === "pending",
          );
          if (hasPresent) {
            item.overallStatus = "present";
          } else if (hasPending) {
            item.overallStatus = "pending";
          } else {
            item.overallStatus = "no_show";
          }
          break;
        }
      }
    },

    bulkUpdateDayStatusesOptimistically(
      state,
      action: PayloadAction<{ attendanceIds: number[]; newStatus: string }>,
    ) {
      const { attendanceIds, newStatus } = action.payload;
      const idSet = new Set(attendanceIds);
      for (const item of state.attendanceGridItems) {
        let changed = false;
        for (const ds of item.dayStatuses) {
          if (idSet.has(ds.attendanceId)) {
            ds.status = newStatus as "pending" | "present" | "no_show";
            changed = true;
          }
        }
        if (changed) {
          const hasPresent = item.dayStatuses.some(
            (ds) => ds.status === "present",
          );
          const hasPending = item.dayStatuses.some(
            (ds) => ds.status === "pending",
          );
          if (hasPresent) {
            item.overallStatus = "present";
          } else if (hasPending) {
            item.overallStatus = "pending";
          } else {
            item.overallStatus = "no_show";
          }
        }
      }
    },

    updateDayNotesOptimistically(
      state,
      action: PayloadAction<{ attendanceId: number; notes: string | null }>,
    ) {
      const { attendanceId, notes } = action.payload;
      for (const item of state.attendanceGridItems) {
        const dayStatus = item.dayStatuses.find(
          (ds) => ds.attendanceId === attendanceId,
        );
        if (dayStatus) {
          dayStatus.notes = notes;
          break;
        }
      }
    },

    setLoadingUpdateAttendance(state, action: PayloadAction<boolean>) {
      state.loadingUpdateAttendance = action.payload;
    },
    setErrorUpdateAttendance(state, action: PayloadAction<string | null>) {
      state.errorUpdateAttendance = action.payload;
    },

    setLoadingExportCsv(state, action: PayloadAction<boolean>) {
      state.loadingExportCsv = action.payload;
    },
    setErrorExportCsv(state, action: PayloadAction<string | null>) {
      state.errorExportCsv = action.payload;
    },

    setLoadingBulkUpdateAttendance(state, action: PayloadAction<boolean>) {
      state.loadingBulkUpdateAttendance = action.payload;
    },
    setErrorBulkUpdateAttendance(state, action: PayloadAction<string | null>) {
      state.errorBulkUpdateAttendance = action.payload;
    },

    setLoadingFinalizeSession(state, action: PayloadAction<boolean>) {
      state.loadingFinalizeSession = action.payload;
    },
    setErrorFinalizeSession(state, action: PayloadAction<string | null>) {
      state.errorFinalizeSession = action.payload;
    },
    setSessionFinalized(state, action: PayloadAction<boolean>) {
      state.sessionFinalized = action.payload;
    },

    clearEnrollmentsWaitlistState() {
      return initialState;
    },
  },
});

export default enrollmentsWaitlistSlice.reducer;

export const {
  setLoadingEnrollments,
  setEnrollmentsError,
  setEnrollments,
  setTotalEnrollments,
  setLoadingWaitlistItems,
  setWaitlistItemsError,
  setWaitlistItems,
  setTotalWaitlistItems,
  setLoadingWaitlistDetails,
  setWaitlistDetailsError,
  setWaitlistDetails,
  setLoadingMoveWaitlist,
  setErrorMoveWaitlist,
  setMoveWaitlistResult,
  setLoadingRemoveWaitlist,
  setErrorRemoveWaitlist,
  setRemoveWaitlistResult,
  setLoadingReplaceAttendee,
  setErrorReplaceAttendee,
  setReplaceAttendeeResult,
  setLoadingReplaceWithEmployee,
  setErrorReplaceWithEmployee,
  setReplaceWithEmployeeResult,
  setLoadingUpdatePosition,
  setErrorUpdatePosition,
  setUpdatePositionResult,
  updateWaitlistItemPositionOptimistically,

  setLoadingMoveEnrollment,
  setErrorMoveEnrollment,
  setMoveEnrollmentResult,

  setLoadingAttendances,
  setAttendancesError,
  setAttendances,
  setTotalAttendances,
  setSelectedAttendanceStatus,

  setLoadingAttendanceGrid,
  setAttendanceGridError,
  setAttendanceGridItems,
  setAttendanceGridSessionDays,
  setTotalAttendanceGridItems,
  updateDayStatusOptimistically,
  bulkUpdateDayStatusesOptimistically,
  updateDayNotesOptimistically,

  setLoadingUpdateAttendance,
  setErrorUpdateAttendance,

  setLoadingExportCsv,
  setErrorExportCsv,

  setLoadingBulkUpdateAttendance,
  setErrorBulkUpdateAttendance,

  setLoadingFinalizeSession,
  setErrorFinalizeSession,
  setSessionFinalized,

  clearEnrollmentsWaitlistState,
} = enrollmentsWaitlistSlice.actions;

export const fetchEventEnrollmentsAction =
  (requestData: FetchEventEnrollmentsRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoadingEnrollments(true));
      dispatch(setEnrollmentsError(null));
      const response = await fetchEventEnrollments(requestData);
      dispatch(setEnrollments(response.data));
      dispatch(setTotalEnrollments(response.meta?.totalCount || 0));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to fetch event enrollments.";
      dispatch(setEnrollmentsError(message));
    } finally {
      dispatch(setLoadingEnrollments(false));
    }
  };

export const fetchEventWaitlistItemsAction =
  (requestData: FetchEventWaitlistItemsRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoadingWaitlistItems(true));
      dispatch(setWaitlistItemsError(null));
      const response = await fetchEventWaitlistItems(requestData);
      dispatch(setWaitlistItems(response.data));
      dispatch(setTotalWaitlistItems(response.meta?.totalCount || 0));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to fetch event waitlist items.";
      dispatch(setWaitlistItemsError(message));
    } finally {
      dispatch(setLoadingWaitlistItems(false));
    }
  };

export const fetchWaitlistDetailsAction =
  (requestData: FetchWaitlistDetailsRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoadingWaitlistDetails(true));
      dispatch(setWaitlistDetailsError(null));
      const response = await fetchWaitlistDetails(requestData);
      dispatch(setWaitlistDetails(response.data));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to fetch waitlist details.";
      dispatch(setWaitlistDetailsError(message));
    } finally {
      dispatch(setLoadingWaitlistDetails(false));
    }
  };

export const moveWaitlistToEnrollmentAction =
  (requestData: MoveWaitlistToEnrollmentRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoadingMoveWaitlist(true));
      dispatch(setErrorMoveWaitlist(null));
      dispatch(setMoveWaitlistResult(null));

      const response: MoveWaitlistToEnrollmentResponse =
        await moveWaitlistToEnrollment(requestData);

      dispatch(setMoveWaitlistResult(response.data));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to move waitlist to enrollment.";
      dispatch(setErrorMoveWaitlist(message));
    } finally {
      dispatch(setLoadingMoveWaitlist(false));
    }
  };

export const removeWaitlistItemAction =
  (requestData: RemoveWaitlistItemRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoadingRemoveWaitlist(true));
      dispatch(setErrorRemoveWaitlist(null));
      dispatch(setRemoveWaitlistResult(null));

      const response: RemoveWaitlistItemResponse =
        await removeWaitlistItem(requestData);
      dispatch(setRemoveWaitlistResult(response.data));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to remove waitlist item.";
      dispatch(setErrorRemoveWaitlist(message));
    } finally {
      dispatch(setLoadingRemoveWaitlist(false));
    }
  };

export const replaceEnrollmentAttendeeAction =
  (requestData: ReplaceEnrollmentAttendeeRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoadingReplaceAttendee(true));
      dispatch(setErrorReplaceAttendee(null));
      dispatch(setReplaceAttendeeResult(null));

      const response: ReplaceEnrollmentAttendeeResponse =
        await replaceEnrollmentAttendee(requestData);
      dispatch(setReplaceAttendeeResult(response.data));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to replace enrollment attendee.";
      dispatch(setErrorReplaceAttendee(message));
    } finally {
      dispatch(setLoadingReplaceAttendee(false));
    }
  };

export const replaceEnrollmentWithEmployeeAction =
  (requestData: ReplaceEnrollmentWithEmployeeRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoadingReplaceWithEmployee(true));
      dispatch(setErrorReplaceWithEmployee(null));
      dispatch(setReplaceWithEmployeeResult(null));

      const response: ReplaceEnrollmentWithEmployeeResponse =
        await replaceEnrollmentWithEmployee(requestData);

      dispatch(setReplaceWithEmployeeResult(response.data));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to replace enrollment with employee.";
      dispatch(setErrorReplaceWithEmployee(message));
    } finally {
      dispatch(setLoadingReplaceWithEmployee(false));
    }
  };

export const updateWaitlistPositionAction =
  (
    requestData: UpdateWaitlistPositionRequest,
    onSuccess?: () => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoadingUpdatePosition(true));
      dispatch(setErrorUpdatePosition(null));
      dispatch(setUpdatePositionResult(null));

      dispatch(
        updateWaitlistItemPositionOptimistically({
          waitlistId: requestData.enrollmentId,
          newPosition: requestData.newPosition,
        }),
      );

      const response: UpdateWaitlistPositionResponse =
        await updateWaitlistPosition(requestData);
      dispatch(setUpdatePositionResult(response.data));

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to update waitlist position.";
      dispatch(setErrorUpdatePosition(message));

      // Revert by re-fetching
      dispatch(fetchEventWaitlistItemsAction({ uuid: requestData.uuid }));
    } finally {
      dispatch(setLoadingUpdatePosition(false));
    }
  };

export const moveEnrollmentToWaitlistAction =
  (requestData: MoveEnrollmentToWaitlistRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoadingMoveEnrollment(true));
      dispatch(setErrorMoveEnrollment(null));
      dispatch(setMoveEnrollmentResult(null));

      const response: MoveEnrollmentToWaitlistResponse =
        await moveEnrollmentToWaitlist(requestData);

      dispatch(setMoveEnrollmentResult(response.data));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to move enrollment to waitlist.";
      dispatch(setErrorMoveEnrollment(message));
    } finally {
      dispatch(setLoadingMoveEnrollment(false));
    }
  };

export const fetchEventSessionAttendancesAction =
  (requestData: FetchEventAttendancesRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoadingAttendances(true));
      dispatch(setAttendancesError(null));
      const response = await fetchEventAttendances(requestData);
      dispatch(setAttendances(response.data));
      dispatch(setTotalAttendances(response.meta?.totalCount || 0));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to fetch event session attendances.";
      dispatch(setAttendancesError(message));
    } finally {
      dispatch(setLoadingAttendances(false));
    }
  };

export const fetchAttendanceGridAction =
  (requestData: FetchEventAttendancesRequest, silent = false): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      if (!silent) {
        dispatch(setLoadingAttendanceGrid(true));
      }
      dispatch(setAttendanceGridError(null));
      const response = await fetchAttendanceGrid(requestData);
      dispatch(setAttendanceGridItems(response.data));
      dispatch(setAttendanceGridSessionDays(response.sessionDays));
      dispatch(setTotalAttendanceGridItems(response.meta?.totalCount || 0));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to fetch attendance grid.";
      dispatch(setAttendanceGridError(message));
    } finally {
      if (!silent) {
        dispatch(setLoadingAttendanceGrid(false));
      }
    }
  };

export const updateEventSessionAttendanceAction =
  (
    attendanceId: number,
    data: UpdateEventSessionAttendanceRequest,
    onSuccess?: () => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoadingUpdateAttendance(true));
      dispatch(setErrorUpdateAttendance(null));

      await updateEventSessionAttendance(attendanceId, data);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to update event session attendance.";
      dispatch(setErrorUpdateAttendance(message));
    } finally {
      dispatch(setLoadingUpdateAttendance(false));
    }
  };

export const exportEventAttendanceCsvAction =
  (
    requestData: ExportEventAttendanceCsvRequest,
    onSuccess?: (blob: Blob) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoadingExportCsv(true));
      dispatch(setErrorExportCsv(null));

      const blob = await exportEventAttendanceCsv(requestData);

      if (onSuccess) {
        onSuccess(blob);
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to export attendance CSV.";
      dispatch(setErrorExportCsv(message));
      throw error;
    } finally {
      dispatch(setLoadingExportCsv(false));
    }
  };

export const bulkUpdateEventSessionAttendanceAction =
  (
    data: BulkUpdateEventSessionAttendanceRequest,
    onSuccess?: () => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoadingBulkUpdateAttendance(true));
      dispatch(setErrorBulkUpdateAttendance(null));

      await bulkUpdateEventSessionAttendance(data);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to bulk update event session attendance.";
      dispatch(setErrorBulkUpdateAttendance(message));
    } finally {
      dispatch(setLoadingBulkUpdateAttendance(false));
    }
  };

export const finalizeEventSessionAction =
  (
    requestData: FinalizeEventSessionRequest,
    onSuccess?: () => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoadingFinalizeSession(true));
      dispatch(setErrorFinalizeSession(null));

      await finalizeEventSession(requestData);
      dispatch(setSessionFinalized(true));

      if (onSuccess) {
        onSuccess();
      }
    } catch {
      // Error toast is handled by the Axios response interceptor
      // which extracts the real error message from errors.detail
      dispatch(setErrorFinalizeSession("Failed to finalize event session."));
    } finally {
      dispatch(setLoadingFinalizeSession(false));
    }
  };
